'use client';
import { useEffect, useState } from 'react';
import {
  Navbar,
  NavbarContent,
  NavbarItem,
  Button,
  cn,
} from '@nextui-org/react';

import { SITE_BRAND } from '@/shared/constants/site-brand';
import { ROUTES } from '@/shared/routes/routes';
import {
  Heart,
  Home,
  Images,
  MessageSquareText,
  PhoneCallIcon,
  ShoppingCartIcon,
  BookOpen,
  Menu,
} from 'lucide-react';

import { Avatar } from '@/components/layout/header/avatar';
import { BottomDrawer } from '@/components/bottom-drawer/bottom-drawer';

import { Montserrat_Alternates as Font } from 'next/font/google';
import Link from 'next/link';
import { CartLink } from '@/components/layout/header/cart-link';
import { FavoritesLink } from '@/components/layout/header/fovorites-link';
import { Search } from '@/components/layout/header/search';
import { useIsAdmin } from '@/shared/hooks/is-admin';
import { usePathname } from '@/shared/navigation';
import { useHeaderSlotValue } from '@/context/header-slot-context';

/**
 * Шрифт используется только для брендинга/заголовка в шапке (font-semibold),
 * поэтому грузим лишь нужные веса. display: 'swap' — текст виден системным
 * шрифтом, пока woff2 не загрузился (Lighthouse: font-display).
 */
const font = Font({
  // cyrillic обязателен: бренд в шапке — кириллица. Без него кириллический
  // woff2 не преложится (preload только для перечисленных сабсетов),
  // грузится поздно и даёт сдвиг при swap (аудит CLS).
  subsets: ['latin', 'cyrillic'],
  // Все использования шрифта — font-semibold; 400/700 не нужны,
  // каждый лишний вес = ещё два preload-файла (latin + cyrillic)
  weight: ['600'],
  display: 'swap',
});

const titles: Record<string, string> = {
  [ROUTES.about]: 'Обо мне',
  [ROUTES.contacts]: 'Контакты',
  [ROUTES.home]: 'Главная',
  [ROUTES.masterclasses]: 'Мастер-классы',
  [ROUTES.me]: 'Профиль',
  [ROUTES['admin-front']]: 'Администрирование',
  [ROUTES.cart]: 'Корзина',
  [ROUTES.register]: 'Регистрация',
  [ROUTES.login]: 'Авторизация',
  [ROUTES['reviews-rules']]: 'Правила отзывов',
  [ROUTES['password-reset']]: 'Восстановление пароля',
  [ROUTES['register-thank']]: 'Спасибо за регистрацию',
  [ROUTES.offer]: 'Оферта',
  [ROUTES.privacy]: 'Политика конфиденциальности',
  [ROUTES['admin-reviews']]: 'Администрирование отзывов',
  [ROUTES['admin-discount']]: 'Администрирование скидок',
  [ROUTES.settings]: 'Настройки',
  [ROUTES['add-review']]: 'Оставить отзыв',
  [ROUTES.meMasterclasses]: 'Купленные мастер-классы',
  [ROUTES.meReviews]: 'Мои отзывы',
  [ROUTES['admin-promocodes']]: 'Администрирование промокодов',
  [ROUTES['admin-masterclasses']]: 'Администрирование мастер-классов',
  [ROUTES['admin-masterclasses-create']]: 'Создание мастер-класса',
  [ROUTES['admin-masterclasses-edit']]: 'Редактирование мастер-класса',
  [ROUTES['admin-categories-order']]: 'Порядок категорий',
  [ROUTES['admin-send-mc']]: 'Отправка МК',
  [ROUTES['admin-analytics']]: 'Аналитика',
  [ROUTES['admin-send-mail']]: 'Отправка писем',
  [ROUTES['admin-buyers-by-mc']]: 'Покупатели по МК',
  [ROUTES['admin-broadcast']]: 'Рассылки',
  [ROUTES['admin-broadcast-new']]: 'Новая рассылка',
  [ROUTES.blog]: 'Работы подписчиков',
  '/not-found': 'Страница не найдена',
};

export const NewHeader = () => {
  const { isAdmin } = useIsAdmin();
  const pathname = usePathname();
  const route =
    pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;
  const { titleCustom, rightContent } = useHeaderSlotValue();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  const items = [
    { title: 'Главная', href: ROUTES.home },
    { title: 'Мастер-классы', href: ROUTES.masterclasses },
    ...(isAdmin ? [{ title: 'Работы подписчиков', href: ROUTES.blog }] : []),
    { title: 'Обо мне', href: ROUTES.about },
    { title: 'Контакты', href: ROUTES.contacts },
  ];

  const mobileMenuItems = [
    {
      id: ROUTES.home,
      href: ROUTES.home,
      title: 'Главная',
      icon: Home,
    },
    {
      id: ROUTES.masterclasses,
      href: ROUTES.masterclasses,
      title: 'Мастер-классы',
      icon: Images,
    },
    ...(isAdmin
      ? [
          {
            id: ROUTES.blog,
            href: ROUTES.blog,
            title: 'Работы подписчиков',
            icon: BookOpen,
          },
        ]
      : []),
    {
      id: ROUTES.about,
      href: ROUTES.about,
      title: 'Обо мне',
      icon: MessageSquareText,
    },
    {
      id: ROUTES.favorites,
      href: ROUTES.favorites,
      title: 'Избранное',
      icon: Heart,
    },
    {
      id: ROUTES.contacts,
      href: ROUTES.contacts,
      title: 'Контакты',
      icon: PhoneCallIcon,
    },
    {
      id: ROUTES.cart,
      href: ROUTES.cart,
      title: 'Корзина',
      icon: ShoppingCartIcon,
    },
  ];
  const leftContent = (
    <>
      {route === ROUTES.home ? (
        <Link href={ROUTES.home}>
          <span
            className={cn(
              'text-lg gap-1 flex items-center text-gray-900 font-semibold',
              font.className,
            )}
          >
            {SITE_BRAND}
          </span>
        </Link>
      ) : (
        <Link href={ROUTES.home}>
          <span
            className={cn(
              'text-lg md:flex hidden text-gray-900 font-semibold',
              font.className,
            )}
          >
            {SITE_BRAND}
          </span>
        </Link>
      )}

      <span
        className={cn(
          'text-lg md:hidden text-gray-900 font-semibold min-w-0 truncate',
          font.className,
        )}
      >
        {route !== ROUTES.home ? (titleCustom ?? titles[route]) : null}
      </span>
    </>
  );

  const right = (
    <>
      {rightContent ? (
        <NavbarItem className="md:hidden flex">{rightContent}</NavbarItem>
      ) : null}
      {rightContent ? (
        <NavbarItem className="hidden md:flex">
          <Search />
        </NavbarItem>
      ) : (
        <NavbarItem>
          <Search />
        </NavbarItem>
      )}

      <FavoritesLink />
      <CartLink />
      <NavbarItem className="hidden md:flex">
        <Avatar key="avatar" />
      </NavbarItem>
      <NavbarItem className="md:hidden flex">
        <Button
          variant="light"
          isIconOnly
          aria-label={isMobileMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
          onClick={() => setIsMobileMenuOpen(true)}
        >
          <Menu size={24} strokeWidth={1.25} className="text-gray-900" />
        </Button>
      </NavbarItem>
    </>
  );

  return (
    <Navbar
      classNames={{
        wrapper: 'md:px-8 px-4',
      }}
      isBordered
      maxWidth="full"
    >
      <NavbarContent justify="start" className="flex-grow w-full min-w-0">
        {/* NavbarContent рендерит <ul>: прямыми детьми должны быть <li>
            (NavbarItem), иначе Lighthouse ругается на структуру списка */}
        <NavbarItem className="flex items-center mr-4 min-w-0 flex-1 md:flex-initial">
          {leftContent}
        </NavbarItem>
        {items.map((item) => (
          <NavbarItem key={item.href} className="hidden md:flex">
            <Link
              className={`text-lg rounded-full  px-6 py-2 text-gray-900 ${
                route === item.href ? 'bg-primary' : ''
              }`}
              href={item.href}
            >
              {item.title}
            </Link>
          </NavbarItem>
        ))}
      </NavbarContent>

      <NavbarContent
        style={{
          flexGrow: 0,
        }}
        className="items-center"
        justify="end"
      >
        {right}
      </NavbarContent>

      <BottomDrawer
        visible={isMobileMenuOpen}
        close={() => setIsMobileMenuOpen(false)}
        showCloseButton
      >
        <div className="grid w-full grid-cols-2 gap-3 pt-4">
          {mobileMenuItems.map((item) => {
            const isActive = route === item.href;
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className={cn(
                  'flex flex-col items-center justify-center gap-1.5 rounded-2xl px-2 py-4 text-center transition-colors',
                  isActive
                    ? 'bg-primary text-gray-900'
                    : 'bg-foreground-50 text-gray-900',
                )}
              >
                <item.icon size={26} strokeWidth={1.25} />
                <span className="text-sm leading-tight">{item.title}</span>
              </Link>
            );
          })}
        </div>
      </BottomDrawer>
    </Navbar>
  );
};
